import { formatDateTime } from '../../../shared/src/utils/formatters';
import type { AuditExportFormat, AuditLogEntry } from '../api';
import { actorLabel, CATEGORY_LABELS, detailEntries, eventLabel, projectLabel, summarize } from './auditEvents';

/**
 * Admin > Audit Logs export files, built in the browser from the pages
 * GET /app/audit-logs/exports/:id/rows serves (see
 * lambda/app-api/audit-handlers.ts for why it is not one download).
 *
 * Both writers take rows page by page, and runExportPages saves a file every
 * EVENTS_PER_FILE events, so a large export is several files the browser
 * saves one after the other.
 */

// ---------------------------------------------------------------------------
// Volume: what the export dialog warns about, by event count
// ---------------------------------------------------------------------------

/** Above this, the dialog says the export is large (over a minute or two). */
export const LARGE_EXPORT_EVENTS = 250_000;

/**
 * Events per file, and the most an Excel export may hold: spreadsheets open
 * about a million rows (Excel 1,048,576, Numbers 1,000,000), CSV included.
 * A full file is this many events plus the header row, plus the export's own
 * row in the last file: 998,002 rows at most, under Numbers' limit. Above
 * it, CSV only, split into files of this size. A multiple of the API's page
 * size (2,000), so files start on a page. Mirrors MAX_XLSX_EXPORT_EVENTS in
 * lambda/app-api/audit-handlers.ts.
 */
export const EVENTS_PER_FILE = 998_000;

/** The most one export may hold (~15-30 min, ~2.5 GB). Mirrors MAX_EXPORT_EVENTS in the API. */
export const MAX_EXPORT_EVENTS = 5_000_000;

/** File size per event, measured on 1M upload events (2026-10-22): CSV ~490 B, XLSX ~100 B deflated. */
const BYTES_PER_EVENT: Record<AuditExportFormat, number> = { csv: 500, xlsx: 100 };

/** Prod pace estimate: 2,000-event pages at 0.3-0.7 s each. */
const EVENTS_PER_SECOND = { slow: 3_000, fast: 6_000 };

export type ExportTier = 'small' | 'large' | 'multi_file' | 'too_large';

export interface ExportPlan {
  tier: ExportTier;
  /** Excel offered: the export fits one sheet */
  excelAllowed: boolean;
  format: AuditExportFormat;
  files: number;
  bytes: number;
  seconds: { min: number; max: number };
}

/** What exporting `events` events in `format` means: files, size, time, and which warning applies. */
export function planExport(events: number, format: AuditExportFormat): ExportPlan {
  const excelAllowed = events <= EVENTS_PER_FILE;
  const effective = excelAllowed ? format : 'csv';
  let tier: ExportTier = 'small';
  if (events > MAX_EXPORT_EVENTS) tier = 'too_large';
  else if (events > EVENTS_PER_FILE) tier = 'multi_file';
  else if (events > LARGE_EXPORT_EVENTS) tier = 'large';
  return {
    tier,
    excelAllowed,
    format: effective,
    files: filesFor(events),
    bytes: events * BYTES_PER_EVENT[effective],
    seconds: { min: events / EVENTS_PER_SECOND.fast, max: events / EVENTS_PER_SECOND.slow },
  };
}

/** How many files an export of `events` events is saved as. */
export function filesFor(events: number): number {
  return Math.max(1, Math.ceil(events / EVENTS_PER_FILE));
}

/** A size to the nearest sensible unit: "less than 1 MB", "8 MB", "310 MB", "1.6 GB". */
export function describeSize(bytes: number): string {
  const mb = bytes / 1e6;
  if (mb < 1) return 'less than 1 MB';
  if (mb < 10) return `${Math.round(mb)} MB`;
  if (mb < 1000) return `${Math.round(mb / 10) * 10} MB`;
  return `${(mb / 1000).toFixed(1)} GB`;
}

/** A duration range, rounded: "under a minute", "about 2 minutes", "roughly 10–20 minutes". */
export function describeDuration(minSeconds: number, maxSeconds: number): string {
  if (maxSeconds < 60) return 'under a minute';
  const lo = Math.max(1, Math.round(minSeconds / 60));
  const hi = Math.max(lo, Math.ceil(maxSeconds / 60));
  return lo === hi ? `about ${lo} minute${lo === 1 ? '' : 's'}` : `roughly ${lo}–${hi} minutes`;
}

/** Time left in a running export: "less than a minute left", "about 6 min left". */
export function describeTimeLeft(seconds: number): string {
  return seconds < 60 ? 'less than a minute left' : `about ${Math.ceil(seconds / 60)} min left`;
}

/** Excel's sheet size, header row included. */
export const XLSX_MAX_ROWS = 1_048_576;

/** Excel's limit on one cell's text. */
const XLSX_MAX_CELL_LENGTH = 32_767;

type ColumnType = 'text' | 'datetime';

interface Column {
  header: string;
  type: ColumnType;
  /** Excel column width, in characters */
  width: number;
  value: (entry: AuditLogEntry) => string;
}

/** The export's columns, times in UTC and in the viewer's zone (`timeZone`). */
export function exportColumns(timeZone: string): Column[] {
  return [
    {
      header: 'Time (UTC)',
      type: 'text',
      width: 22,
      value: (e) => new Date(e.created_at).toISOString().replace(/\.\d+Z$/, 'Z'),
    },
    {
      header: `Time (${timeZone})`,
      type: 'datetime',
      width: 20,
      value: (e) => formatDateTime(e.created_at, { timeZone }),
    },
    { header: 'User', type: 'text', width: 32, value: (e) => actorLabel(e.actor) },
    { header: 'User name', type: 'text', width: 22, value: (e) => (e.actor.type === 'user' ? e.actor.name ?? '' : '') },
    { header: 'Category', type: 'text', width: 12, value: (e) => CATEGORY_LABELS[e.category] ?? e.category },
    { header: 'Action', type: 'text', width: 26, value: (e) => eventLabel(e.event) },
    { header: 'Project', type: 'text', width: 22, value: (e) => projectLabel(e.project) },
    { header: 'Item', type: 'text', width: 48, value: summarize },
    {
      header: 'Details',
      type: 'text',
      width: 60,
      value: (e) => detailEntries(e).map(([label, text]) => `${label}: ${text}`).join('; '),
    },
    { header: 'User agent', type: 'text', width: 40, value: (e) => e.user_agent ?? '' },
    { header: 'Event', type: 'text', width: 24, value: (e) => e.event },
    { header: 'Event ID', type: 'text', width: 38, value: (e) => e.id },
  ];
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * One RFC 4180 field. A value starting with = + - @ (or a tab / CR) is
 * prefixed with ' so a spreadsheet shows it rather than running it as a
 * formula: file and folder names are typed by any user of the organization.
 */
export function csvField(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export interface ExportWriter {
  addRows(entries: AuditLogEntry[]): void;
  finish(): Promise<Blob>;
}

/**
 * UTF-8 with a BOM (Excel otherwise reads accented names as Latin-1), CRLF
 * lines. Each page leaves the JS heap as a Blob of its own, which the browser
 * may keep on disk: a million events are ~0.5 GB of text, enough to crash a
 * tab held as strings.
 */
export function createCsvWriter(timeZone: string): ExportWriter {
  const columns = exportColumns(timeZone);
  const parts: Blob[] = [new Blob(['\uFEFF' + columns.map((c) => csvField(c.header)).join(',') + '\r\n'])];
  return {
    addRows(entries) {
      parts.push(new Blob([entries.map((e) => columns.map((c) => csvField(c.value(e))).join(',') + '\r\n').join('')]));
    },
    async finish() {
      return new Blob(parts, { type: 'text/csv;charset=utf-8' });
    },
  };
}

// ---------------------------------------------------------------------------
// XLSX: a minimal SpreadsheetML workbook, one sheet of inline strings
// ---------------------------------------------------------------------------

/** XML text: escaped, without the control characters XML 1.0 forbids, within Excel's cell limit. */
export function xmlText(value: string): string {
  return value
    .slice(0, XLSX_MAX_CELL_LENGTH)
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** A, B, …, Z, AA, … */
export function columnName(index: number): string {
  let name = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  }
  return name;
}

/** `yyyy-mm-dd HH:mm:ss` wall-clock text → Excel serial date (days since 1899-12-30). */
export function excelSerial(wallClock: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(wallClock);
  if (!m) return null;
  const [year, month, day, hour, minute, second] = m.slice(1).map(Number);
  return Date.UTC(year, month - 1, day, hour, minute, second) / 86_400_000 + 25_569;
}

/** Style ids in xl/styles.xml */
const STYLE_HEADER = 1;
const STYLE_DATETIME = 2;

export const XLSX_STATIC_PARTS: Record<string, string> = {
  '[Content_Types].xml':
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    '</Types>',
  '_rels/.rels':
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
    '</Relationships>',
  'xl/workbook.xml':
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    '<sheets><sheet name="Audit logs" sheetId="1" r:id="rId1"/></sheets>' +
    '</workbook>',
  'xl/_rels/workbook.xml.rels':
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
    '</Relationships>',
  'xl/styles.xml':
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<numFmts count="1"><numFmt numFmtId="164" formatCode="yyyy-mm-dd hh:mm:ss"/></numFmts>' +
    '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
    '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    '<cellXfs count="3">' +
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
    '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
    '</cellXfs>' +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>',
};

/** Sheet XML up to the opening of sheetData, then the header row: frozen, column widths set. */
export function xlsxSheetStart(columns: Column[]): string {
  const cols = columns.map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.width}" customWidth="1"/>`).join('');
  const header = columns
    .map((c, i) => `<c r="${columnName(i)}1" t="inlineStr" s="${STYLE_HEADER}"><is><t>${xmlText(c.header)}</t></is></c>`)
    .join('');
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `<cols>${cols}</cols>` +
    `<sheetData><row r="1">${header}</row>`
  );
}

/** Rows `firstRow`… (1-based, the header being row 1). */
export function xlsxSheetRows(columns: Column[], entries: AuditLogEntry[], firstRow: number): string {
  return entries
    .map((entry, i) => {
      const r = firstRow + i;
      const cells = columns
        .map((c, col) => {
          const ref = `${columnName(col)}${r}`;
          const value = c.value(entry);
          const serial = c.type === 'datetime' ? excelSerial(value) : null;
          if (serial !== null) return `<c r="${ref}" s="${STYLE_DATETIME}"><v>${serial}</v></c>`;
          if (!value) return '';
          return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlText(value)}</t></is></c>`;
        })
        .join('');
      return `<row r="${r}">${cells}</row>`;
    })
    .join('');
}

/** Close sheetData, with a filter on every column of the `lastRow` rows. */
export function xlsxSheetEnd(columns: Column[], lastRow: number): string {
  return `</sheetData><autoFilter ref="A1:${columnName(columns.length - 1)}${lastRow}"/></worksheet>`;
}

// ---------------------------------------------------------------------------
// The page loop
// ---------------------------------------------------------------------------

export interface ExportPage {
  rows: AuditLogEntry[];
  next_cursor: string | null;
}

/** Where an export stands: which file comes next and where it starts. */
export interface ExportPosition {
  exportId: string;
  format: AuditExportFormat;
  /** Rows the export serves, its own row included */
  total: number;
  files: number;
  /** 1-based number of the next file to write; files before it are saved */
  file: number;
  /** Cursor of that file's first page; undefined for the export's first page */
  cursor?: string;
  /** Rows in the files already saved */
  fetched: number;
}

export interface ExportProgress {
  fetched: number;
  total: number;
  file: number;
  files: number;
  /** Null until a few seconds of pace have been measured */
  secondsLeft: number | null;
}

/** Why an export stopped, and where to continue it from. */
export class ExportStoppedError<P extends ExportPosition = ExportPosition> extends Error {
  constructor(
    readonly reason: 'cancelled' | 'failed',
    readonly resumeAt: P,
    readonly cause?: unknown
  ) {
    super(reason === 'cancelled' ? 'Export cancelled' : cause instanceof Error ? cause.message : 'Export failed');
  }
}

interface RunExportPagesOptions<P extends ExportPosition> {
  position: P;
  fetchPage: (cursor: string | undefined) => Promise<ExportPage>;
  createWriter: () => Promise<ExportWriter>;
  saveFile: (blob: Blob, file: number, files: number) => void;
  onProgress: (progress: ExportProgress) => void;
  isCancelled: () => boolean;
  now?: () => number;
  /** Tests use small files */
  eventsPerFile?: number;
}

/** Seconds of pace measured before a time-left estimate is shown. */
const PACE_WARMUP_SECONDS = 3;

/**
 * Fetch an export's pages from `position` on and save them as files of
 * EVENTS_PER_FILE events, the export's own row ending the last one. The next
 * page is fetched while the current one is written. A failure or a cancel
 * throws ExportStoppedError with the start of the file being written (the
 * position's other fields carried over): the files before it are saved, and
 * the run can continue from there.
 */
export async function runExportPages<P extends ExportPosition>({
  position,
  fetchPage,
  createWriter,
  saveFile,
  onProgress,
  isCancelled,
  now = Date.now,
  eventsPerFile = EVENTS_PER_FILE,
}: RunExportPagesOptions<P>): Promise<void> {
  let file = position.file;
  let fileStart = { cursor: position.cursor, fetched: position.fetched };
  let fetched = position.fetched;
  let rowsInFile = 0;
  const startedAt = now();
  const resumeAt = (): P => ({ ...position, file, cursor: fileStart.cursor, fetched: fileStart.fetched });

  let pending: Promise<ExportPage> | null = fetchPage(position.cursor);
  try {
    let writer = await createWriter();
    while (pending) {
      if (isCancelled()) throw new ExportStoppedError('cancelled', resumeAt());
      const page: ExportPage = await pending;
      const nextCursor = page.next_cursor ?? undefined;
      pending = nextCursor ? fetchPage(nextCursor) : null;

      writer.addRows(page.rows);
      rowsInFile += page.rows.length;
      fetched += page.rows.length;
      const elapsed = (now() - startedAt) / 1000;
      const pace = elapsed >= PACE_WARMUP_SECONDS ? (fetched - position.fetched) / elapsed : 0;
      onProgress({
        fetched,
        total: position.total,
        file,
        files: position.files,
        secondsLeft: pace > 0 ? (position.total - fetched) / pace : null,
      });

      // A full file, and not the last one: save it, start the next
      if (rowsInFile >= eventsPerFile && file < position.files && pending) {
        saveFile(await writer.finish(), file, position.files);
        file += 1;
        rowsInFile = 0;
        fileStart = { cursor: nextCursor, fetched };
        writer = await createWriter();
      }
    }
    saveFile(await writer.finish(), file, position.files);
  } catch (err) {
    // A prefetch still in flight must not reject unobserved (unhandledrejection)
    pending?.catch(() => {});
    if (err instanceof ExportStoppedError) throw err;
    throw new ExportStoppedError('failed', resumeAt(), err);
  }
}
